import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Share, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * iOS install prompt — Safari doesn't fire `beforeinstallprompt`, so iPhone
 * users need a manual nudge to install Travelix to their home screen.
 *
 * Shows once per user (persisted in localStorage). Dismissible. Only appears
 * on iOS Safari when the app isn't already running in standalone mode, and
 * never in the native (Capacitor) app: its WKWebView has an iPhone user agent
 * and no `navigator.standalone`, so the UA check alone would tell App Store
 * users to install the web app.
 */

const DISMISSED_KEY = "travelix:install-prompt-dismissed";
const DELAY_MS = 1200;

interface Nav extends Navigator {
  standalone?: boolean;
}

function isIosSafari(): boolean {
  if (typeof window === "undefined") return false;
  if (Capacitor.isNativePlatform()) return false;
  const ua = window.navigator.userAgent;
  const nav = window.navigator as Nav;
  const isIos = /iPhone|iPad|iPod/.test(ua) && !("MSStream" in window);
  const isStandalone = nav.standalone === true;
  return isIos && !isStandalone;
}

export function InstallPrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isIosSafari()) return;
    try {
      if (window.localStorage.getItem(DISMISSED_KEY)) return;
    } catch {
      /* storage disabled — show once anyway */
    }
    const t = window.setTimeout(() => setVisible(true), DELAY_MS);
    return () => window.clearTimeout(t);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      /* non-fatal */
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Install Travelix"
      data-ad-avoid=""
      className={cn(
        "fixed inset-x-3 bottom-3 z-40 mx-auto max-w-sm rounded-xl",
        "bg-bg-elevated border border-line shadow-warm-3 p-4 pr-3",
        "animate-in slide-in-from-bottom-4 fade-in duration-300",
      )}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-ink-subtle hover:bg-bg-sunken hover:text-ink"
      >
        <X className="h-4 w-4" />
      </button>
      <p
        className="font-display text-[18px] leading-tight tracking-tight text-ink m-0"
        style={{ fontVariationSettings: `"opsz" 36, "wght" 500` }}
      >
        Install Travelix on your Home Screen
      </p>
      <p className="mt-1.5 text-[13px] leading-snug text-ink-muted m-0">
        Tap{" "}
        <span className="inline-flex items-center align-middle rounded-md bg-bg-sunken px-1.5 py-0.5">
          <Share className="h-3.5 w-3.5" />
        </span>
        {" "}then{" "}
        <span className="font-semibold text-ink">Add to Home Screen</span>
        {" "}for the full-screen app.
      </p>
    </div>
  );
}
